import { useState, useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Textarea } from "@/components/ui/textarea"
import {
    Database,
    MessageCircleQuestion,
    Pencil,
    Sparkles,
    Trash2,
    Type,
    Mail,
    Phone,
    Hash,
    Link,
    Calendar,
    ChevronDown,
    ToggleLeft,
    List,
    CheckSquare,
    Lock
} from 'lucide-react'
import { cn } from '@/lib/utils'

interface starterProps {
    addField: any;
    editField: any;
    setModalVisible: any;
    field: any;
    isQuestion?: boolean;
    isAiLabel?: boolean;
}

interface InputType {
    value: string;
    displayName: string;
    icon: React.ReactNode;
    description: string;
}

const supportedInputTypes: InputType[] = [
    { value: 'text', displayName: 'Text', icon: <Type className="h-4 w-4" />, description: 'Single line text input' },
    { value: 'email', displayName: 'Email', icon: <Mail className="h-4 w-4" />, description: 'Email address validation' },
    { value: 'tel', displayName: 'Phone', icon: <Phone className="h-4 w-4" />, description: 'Phone number input' },
    { value: 'number', displayName: 'Number', icon: <Hash className="h-4 w-4" />, description: 'Numeric values only' },
    { value: 'url', displayName: 'URL', icon: <Link className="h-4 w-4" />, description: 'Website URL validation' },
    { value: 'date', displayName: 'Date', icon: <Calendar className="h-4 w-4" />, description: 'Date picker input' },
    { value: 'dropdown', displayName: 'Dropdown', icon: <ChevronDown className="h-4 w-4" />, description: 'Single selection from options' },
    { value: 'checkbox', displayName: 'Yes/No', icon: <ToggleLeft className="h-4 w-4" />, description: 'Boolean true/false value' },
    { value: 'multiselect', displayName: 'Multi-Select', icon: <CheckSquare className="h-4 w-4" />, description: 'Multiple selections allowed' }
];

type FieldCategory = 'field' | 'question' | 'ai_label';

function getInitialCategory(field: any, isQuestion: boolean, isAiLabel: boolean): FieldCategory {
    if (field?.ai_label || isAiLabel) return 'ai_label';
    if (field?.question || isQuestion) return 'question';
    return 'field';
}

export default function CreateField({ addField, editField, setModalVisible, field, isQuestion = false, isAiLabel = false }: starterProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [fieldLabel, setFieldLabel] = useState(field ? field.label : '');
    const [fieldName, setFieldName] = useState(field ? field.name : '');
    const [fieldType, setFieldType] = useState(field ? field.type : 'text');
    const [fieldRequired, setFieldRequired] = useState(field ? field.required : false);
    const [fieldOptions, setFieldOptions] = useState<string[]>(field?.options || []);
    const [newOption, setNewOption] = useState('');
    const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
    const [editingOptionValue, setEditingOptionValue] = useState('');
    const [fieldHidden, setFieldHidden] = useState(field ? field.hidden || false : false);
    const [fieldDescription, setFieldDescription] = useState(field ? field.description || '' : '');
    const [fieldRequireCountryCode, setFieldRequireCountryCode] = useState(field ? field.requireCountryCode || false : false);
    const [fieldCategory, setFieldCategory] = useState<FieldCategory>(() => getInitialCategory(field, isQuestion, isAiLabel));

    const isEditing = !!field;
    const selectedTypeInfo = supportedInputTypes.find(t => t.value === fieldType);
    const effectiveIsQuestion = fieldCategory === 'question';
    const effectiveIsAiLabel = fieldCategory === 'ai_label';
    const CategoryIcon = effectiveIsAiLabel ? Sparkles : effectiveIsQuestion ? MessageCircleQuestion : Database;
    const systemFieldNamesSet = ['email', 'first_name', 'last_name', 'phone', 'company', 'job_title'];
    const isSystemField = isEditing && systemFieldNamesSet.includes(fieldName);

    function closeModal() {
        setModalVisible(false)
        setIsOpen(false)
    }

    function validateField() {
        if (!fieldLabel.trim()) {
            return { valid: false, error: "Field label is required" };
        }

        if (fieldType === 'dropdown' && fieldOptions.length < 1) {
            return { valid: false, error: "Dropdown fields require at least one option" };
        }

        if (fieldType === 'multiselect' && fieldOptions.length < 1) {
            return { valid: false, error: "Multi-select fields require at least one option" };
        }

        return { valid: true, error: null };
    }

    function updateField() {
        const validation = validateField();
        if (!validation.valid) {
            alert(validation.error);
            return;
        }

        const updated = editField({
            label: fieldLabel,
            type: fieldType,
            required: fieldRequired,
            name: fieldName,
            options: (fieldType === 'dropdown' || fieldType === 'multiselect') ? fieldOptions : undefined,
            hidden: effectiveIsAiLabel ? false : fieldHidden,
            question: effectiveIsQuestion ? true : undefined,
            ai_label: effectiveIsAiLabel ? true : undefined,
            description: effectiveIsAiLabel ? fieldDescription : undefined,
            requireCountryCode: fieldType === 'tel' ? fieldRequireCountryCode : undefined,
        });

        if (updated) {
            closeModal();
            setModalVisible(false);
        }
    }

    function addNewField() {
        const validation = validateField();
        if (!validation.valid) {
            alert(validation.error);
            return;
        }

        const added = addField({
            label: fieldLabel,
            type: fieldType,
            required: fieldRequired,
            name: '',
            options: (fieldType === 'dropdown' || fieldType === 'multiselect') ? fieldOptions : undefined,
            hidden: effectiveIsAiLabel ? false : fieldHidden,
            question: effectiveIsQuestion ? true : undefined,
            ai_label: effectiveIsAiLabel ? true : undefined,
            description: effectiveIsAiLabel ? fieldDescription : undefined,
            requireCountryCode: fieldType === 'tel' ? fieldRequireCountryCode : undefined,
        });

        if (added) {
            closeModal();
            setModalVisible(false);
        }
    }

    const addOption = () => {
        if (newOption.trim() && !fieldOptions.includes(newOption.trim())) {
            setFieldOptions([...fieldOptions, newOption.trim()]);
            setNewOption('');
        }
    };

    const removeOption = (optionToRemove: string) => {
        setFieldOptions(fieldOptions.filter(option => option !== optionToRemove));
    };

    const startEditingOption = (index: number) => {
        setEditingOptionIndex(index);
        setEditingOptionValue(fieldOptions[index]);
    };

    const cancelEditingOption = () => {
        setEditingOptionIndex(null);
        setEditingOptionValue('');
    };

    const saveEditedOption = () => {
        if (editingOptionIndex === null) return;
        const trimmed = editingOptionValue.trim();
        const isDuplicate = fieldOptions.some(
            (option, index) => index !== editingOptionIndex && option === trimmed
        );
        if (trimmed && !isDuplicate) {
            setFieldOptions(fieldOptions.map((option, index) =>
                index === editingOptionIndex ? trimmed : option
            ));
        }
        cancelEditingOption();
    };

    useEffect(() => {
        setIsOpen(true)
    }, []);

    return (
        <Dialog open={isOpen} onOpenChange={closeModal}>
            <DialogContent
                className="max-w-xl gap-0 overflow-hidden p-0"
                onEscapeKeyDown={(e) => {
                    if (editingOptionIndex !== null) {
                        e.preventDefault();
                        cancelEditingOption();
                    }
                }}
            >
                {/* Header */}
                <DialogHeader className="border-b px-6 py-5 text-left">
                    <DialogTitle className="flex items-center gap-2 text-lg">
                        <CategoryIcon className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
                        {isEditing ? 'Edit' : 'Create'} {effectiveIsAiLabel ? 'AI label' : effectiveIsQuestion ? 'question' : 'field'}
                    </DialogTitle>
                    <DialogDescription className="text-sm">
                        {effectiveIsAiLabel
                            ? 'Classify information from the conversation without asking the visitor.'
                            : effectiveIsQuestion
                                ? 'Ask the visitor a qualifying question during the conversation.'
                                : 'Collect structured contact or profile information from the visitor.'
                        }
                    </DialogDescription>
                </DialogHeader>

                <ScrollArea className="max-h-[65vh]">
                    <div className="space-y-5 p-6">
                        {/* System Field Indicator */}
                        {isSystemField && (
                            <div className="flex items-center gap-3 rounded-lg border bg-muted/20 p-3">
                                <Lock className="h-4 w-4 shrink-0 text-muted-foreground" />
                                <div>
                                    <div className="text-sm font-medium">System field</div>
                                    <div className="text-xs text-muted-foreground">
                                        System name: <span className="font-mono font-medium text-foreground">{fieldName}</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Category Selector */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Item type</Label>
                            <div className="grid grid-cols-3 gap-2">
                                {([
                                    { value: 'field' as FieldCategory, label: 'Field', icon: <Database className="h-3.5 w-3.5" /> },
                                    { value: 'question' as FieldCategory, label: 'Question', icon: <MessageCircleQuestion className="h-3.5 w-3.5" /> },
                                    { value: 'ai_label' as FieldCategory, label: 'AI label', icon: <Sparkles className="h-3.5 w-3.5" /> },
                                ]).map((cat) => (
                                    <button
                                        key={cat.value}
                                        type="button"
                                        onClick={() => setFieldCategory(cat.value)}
                                        className={cn(
                                            "flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors",
                                            fieldCategory === cat.value
                                                ? "border-foreground/20 bg-muted/30 text-foreground"
                                                : "border-border/70 text-muted-foreground hover:border-foreground/15 hover:text-foreground"
                                        )}
                                    >
                                        {cat.icon}
                                        {cat.label}
                                        <span className={cn(
                                            "ml-auto flex h-3.5 w-3.5 items-center justify-center rounded-full border",
                                            fieldCategory === cat.value
                                                ? "border-primary"
                                                : "border-muted-foreground/40"
                                        )}>
                                            {fieldCategory === cat.value && (
                                                <span className="h-1.5 w-1.5 rounded-full bg-primary" />
                                            )}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Label/Question Input */}
                        <div className="space-y-2">
                            <Label htmlFor="formFieldLabel" className="text-sm font-medium">
                                {effectiveIsAiLabel ? 'Label Name' : effectiveIsQuestion ? 'Question Text' : 'Field Label'}
                            </Label>
                            <Input
                                id="formFieldLabel"
                                value={fieldLabel}
                                onChange={(e) => setFieldLabel(e.target.value)}
                                placeholder={effectiveIsAiLabel ? "e.g., Visitor Intent" : effectiveIsQuestion ? "e.g., What's your timeline for this project?" : "e.g., Company Name"}
                                autoFocus
                            />
                            {isEditing && !isSystemField && (
                                <p className="text-xs text-muted-foreground">
                                    System name: <span className="font-mono font-medium text-foreground">{fieldName}</span>
                                </p>
                            )}
                        </div>

                        {/* Description - AI Labels only */}
                        {effectiveIsAiLabel && (
                            <div className="space-y-2">
                                <Label htmlFor="formFieldDescription" className="text-sm font-medium">
                                    Description
                                </Label>
                                <p className="text-xs text-muted-foreground">
                                    Tell the AI what this label is for and when it should be filled out
                                </p>
                                <Textarea
                                    id="formFieldDescription"
                                    value={fieldDescription}
                                    onChange={(e) => setFieldDescription(e.target.value)}
                                    placeholder="e.g., Classify the visitor's primary intent based on the conversation — such as 'Pricing Inquiry', 'Technical Support', 'Partnership', or 'General Question'. Fill this out once you have enough context."
                                    rows={3}
                                    className="resize-y"
                                />
                            </div>
                        )}

                        {/* Field Type Selection */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">Response type</Label>
                            <Select value={fieldType} onValueChange={setFieldType}>
                                <SelectTrigger>
                                    <SelectValue>
                                        {selectedTypeInfo && (
                                            <div className="flex items-center gap-2">
                                                {selectedTypeInfo.icon}
                                                <span>{selectedTypeInfo.displayName}</span>
                                            </div>
                                        )}
                                    </SelectValue>
                                </SelectTrigger>
                                <SelectContent>
                                    {supportedInputTypes.map((type) => (
                                        <SelectItem key={type.value} value={type.value}>
                                            <div className="flex items-center gap-3">
                                                <div className="text-muted-foreground">
                                                    {type.icon}
                                                </div>
                                                <div>
                                                    <div className="font-medium">{type.displayName}</div>
                                                    <div className="text-xs text-muted-foreground">{type.description}</div>
                                                </div>
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {/* Options for Dropdown/Multi-select */}
                        {(fieldType === 'dropdown' || fieldType === 'multiselect') && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <Label className="text-sm font-medium">
                                        {fieldType === 'dropdown' ? 'Dropdown' : 'Multi-select'} options
                                    </Label>
                                    {fieldOptions.length > 0 && (
                                        <Badge variant="secondary" className="text-xs">
                                            {fieldOptions.length} option{fieldOptions.length !== 1 ? 's' : ''}
                                        </Badge>
                                    )}
                                </div>
                                <div className="flex gap-2">
                                    <Input
                                        value={newOption}
                                        onChange={(e) => setNewOption(e.target.value)}
                                        placeholder="Type an option and press Enter"
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault();
                                                addOption();
                                            }
                                        }}
                                        className="flex-1"
                                    />
                                    <Button onClick={addOption} variant="outline" size="sm">
                                        Add
                                    </Button>
                                </div>
                                {fieldOptions.length > 0 ? (
                                    <div className="max-h-[200px] overflow-y-auto rounded-lg border divide-y">
                                        {fieldOptions.map((option, index) => (
                                            <div
                                                key={index}
                                                className="group flex items-center justify-between gap-2 px-3 py-2.5 transition-colors hover:bg-muted/30"
                                            >
                                                {editingOptionIndex === index ? (
                                                    <div className="flex flex-1 items-center gap-2">
                                                        <div className="h-5 w-5 rounded bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground flex-shrink-0">
                                                            {index + 1}
                                                        </div>
                                                        <Input
                                                            value={editingOptionValue}
                                                            onChange={(e) => setEditingOptionValue(e.target.value)}
                                                            onKeyDown={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    e.preventDefault();
                                                                    saveEditedOption();
                                                                } else if (e.key === 'Escape') {
                                                                    e.preventDefault();
                                                                    cancelEditingOption();
                                                                }
                                                            }}
                                                            onBlur={saveEditedOption}
                                                            className="h-7 flex-1 text-sm"
                                                            autoFocus
                                                        />
                                                    </div>
                                                ) : (
                                                    <>
                                                        <div className="flex items-center gap-2 min-w-0">
                                                            <div className="h-5 w-5 rounded bg-muted flex items-center justify-center text-xs font-medium text-muted-foreground flex-shrink-0">
                                                                {index + 1}
                                                            </div>
                                                            <span className="text-sm truncate">{option}</span>
                                                        </div>
                                                        <div className="flex items-center flex-shrink-0">
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-muted-foreground hover:text-foreground"
                                                                onClick={() => startEditingOption(index)}
                                                                aria-label={`Edit ${option}`}
                                                            >
                                                                <Pencil className="h-3.5 w-3.5" />
                                                            </Button>
                                                            <Button
                                                                type="button"
                                                                variant="ghost"
                                                                size="icon"
                                                                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                                                                onClick={() => removeOption(option)}
                                                                aria-label={`Remove ${option}`}
                                                            >
                                                                <Trash2 className="h-3.5 w-3.5" />
                                                            </Button>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-center py-6 border border-dashed rounded-lg">
                                        <List className="h-6 w-6 text-muted-foreground mx-auto mb-2" />
                                        <p className="text-sm text-muted-foreground">No options added yet</p>
                                        <p className="text-xs text-muted-foreground mt-1">Add at least one option above</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Settings */}
                        <div className="space-y-3">
                            <Label className="text-sm font-medium">Settings</Label>
                            <div className="overflow-hidden rounded-lg border divide-y">
                                {/* Required Toggle */}
                                <div className="flex items-center justify-between gap-6 p-4">
                                    <div>
                                        <span className="text-sm font-medium">Required</span>
                                        <p className="text-xs text-muted-foreground">
                                            {effectiveIsAiLabel
                                                ? 'AI must fill this label before the conversation is considered complete'
                                                : `Visitor must provide this ${effectiveIsQuestion ? 'answer' : 'information'}`
                                            }
                                        </p>
                                    </div>
                                    <Switch
                                        checked={fieldRequired}
                                        onCheckedChange={setFieldRequired}
                                    />
                                </div>

                                {/* Hidden Toggle - not shown for AI labels (always hidden by nature) */}
                                {!effectiveIsAiLabel && (
                                    <div className="flex items-center justify-between gap-6 p-4">
                                        <div>
                                            <span className="text-sm font-medium">Hidden</span>
                                            <p className="text-xs text-muted-foreground">
                                                Hide from the traditional form view
                                            </p>
                                        </div>
                                        <Switch
                                            checked={fieldHidden}
                                            onCheckedChange={setFieldHidden}
                                        />
                                    </div>
                                )}

                                {/* Country Code Toggle - phone fields only */}
                                {fieldType === 'tel' && !effectiveIsAiLabel && (
                                    <div className="flex items-center justify-between gap-6 p-4">
                                        <div>
                                            <span className="text-sm font-medium">Require country code</span>
                                            <p className="text-xs text-muted-foreground">
                                                Require a leading + country code, such as +15551234567
                                            </p>
                                        </div>
                                        <Switch
                                            checked={fieldRequireCountryCode}
                                            onCheckedChange={setFieldRequireCountryCode}
                                        />
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </ScrollArea>

                {/* Footer */}
                <DialogFooter className="border-t px-6 py-4">
                    <Button variant="outline" onClick={closeModal}>
                        Cancel
                    </Button>
                    <Button onClick={isEditing ? updateField : addNewField}>
                        {isEditing ? 'Save changes' : `Create ${effectiveIsAiLabel ? 'AI label' : effectiveIsQuestion ? 'question' : 'field'}`}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}